import React, { useRef, useEffect, type ComponentType, type RefObject } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import type { issueTable_nativeIssueTable$key as IssueResultsFragment } from '@atlassian/jira-relay/src/__generated__/issueTable_nativeIssueTable.graphql';
import {
	COLUMN_ID_MEATBALL_MENU,
	COLUMN_RESIZE_HANDLE_FULL_HEIGHT,
	COLUMN_RESIZE_HANDLE_HEADER_ONLY,
} from '../../common/constants';
import type {
	NoColumnsProps,
	Column,
	ColumnId,
	TableInjectedCellProps,
	NoIssuesProps,
} from '../../common/types';
import { Overlay } from '../../common/ui/overlay';
import { IssueTableSkeleton } from '../../common/ui/skeleton';
import { Header } from './header';
import messages from './messages';
import NoColumnsRenderer from './no-columns-renderer';
import NoIssuesRenderer from './no-issues-renderer';
import TableBody from './table-body';
import TableHeaderOld from './table-header-old';

export type Props = {
	columns: Column[];
	NoIssues?: ComponentType<NoIssuesProps>;
	canViewColumnConfiguration: boolean;
	canEditColumnConfiguration: boolean;
	hasFixedTableLayout: boolean;
	hasInlineEditing: boolean;
	isColumnResizingEnabled: boolean;
	issueResults: IssueResultsFragment;
	loading: boolean;
	NoColumns?: ComponentType<NoColumnsProps>;
	onColumnsChange?: (columns: Column[]) => void;
	onResizeColumn?: (columnId: ColumnId, width: number | undefined) => void;
	enableRowDragging: boolean;
	tableInjectedProps: TableInjectedCellProps;
	tableRef: RefObject<HTMLTableElement>;
};

const IssueTable = ({
	columns,
	NoIssues,
	canViewColumnConfiguration,
	canEditColumnConfiguration,
	hasInlineEditing,
	hasFixedTableLayout,
	isColumnResizingEnabled,
	issueResults,
	loading,
	NoColumns,
	onColumnsChange,
	onResizeColumn,
	enableRowDragging,
	tableInjectedProps,
	tableRef,
}: Props) => {
	const isInitialDataLoaded = useRef(false);

	// TODO: delete `isInitialDataLoaded` and effect when cleaning up jsv-one-issue-table-spreadsheet-ui_ygrnf
	useEffect(() => {
		if (
			!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') &&
			!loading &&
			!isInitialDataLoaded.current
		) {
			isInitialDataLoaded.current = true;
		}
	}, [loading]);

	const { formatMessage } = useIntl();
	const issueResultsData = useFragment<IssueResultsFragment>(
		graphql`
			fragment issueTable_nativeIssueTable on JiraIssueConnection
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				edges {
					__typename # only interested in edge presence to handle empty state
				}
				...tableBody_nativeIssueTable @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
			}
		`,
		issueResults,
	);

	// Show the skeleton if we are loading on initial render as we may only have partial row data
	if (!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && loading && !isInitialDataLoaded.current) {
		return <IssueTableSkeleton />;
	}

	if (!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && !issueResultsData.edges?.length) {
		return loading ? <IssueTableSkeleton /> : <NoIssuesRenderer NoIssues={NoIssues} />;
	}

	const hasColumnConfiguration = canViewColumnConfiguration && canEditColumnConfiguration;

	const showNoIssues = !issueResultsData.edges?.length;
	const showNoColumns =
		columns.length === 0 || (columns.length === 1 && columns[0].id === COLUMN_ID_MEATBALL_MENU);

	let tableHeader;
	if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
		tableHeader = (
			<Header
				columns={columns}
				canViewColumnConfiguration={canViewColumnConfiguration}
				canEditColumnConfiguration={canEditColumnConfiguration}
				hasFixedTableLayout={hasFixedTableLayout}
				isColumnResizingEnabled={isColumnResizingEnabled}
				resizeHandleType={
					showNoIssues || showNoColumns
						? COLUMN_RESIZE_HANDLE_HEADER_ONLY
						: COLUMN_RESIZE_HANDLE_FULL_HEIGHT
				}
				onColumnsChange={onColumnsChange}
				onResizeColumn={onResizeColumn}
				tableRef={tableRef}
			/>
		);
	} else {
		tableHeader = (
			<TableHeaderOld
				columns={columns}
				hasColumnConfiguration={hasColumnConfiguration}
				onColumnsChange={onColumnsChange}
				tableRef={tableRef}
			/>
		);
	}

	if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && showNoIssues) {
		return (
			<>
				<Table
					hasFixedTableLayout={hasFixedTableLayout}
					aria-label={formatMessage(messages.issuesTableLabel)}
					ref={tableRef}
				>
					{tableHeader}
				</Table>
				<NoIssuesRenderer NoIssues={NoIssues} isLoading={loading} />
			</>
		);
	}

	if (showNoColumns) {
		return (
			<>
				{ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && canViewColumnConfiguration && (
					<Table
						hasFixedTableLayout={hasFixedTableLayout}
						aria-label={formatMessage(messages.issuesTableLabel)}
						ref={tableRef}
					>
						{tableHeader}
					</Table>
				)}
				{!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && hasColumnConfiguration && (
					<Table
						hasFixedTableLayout={hasFixedTableLayout}
						aria-label={formatMessage(messages.issuesTableLabel)}
						ref={tableRef}
					>
						{tableHeader}
					</Table>
				)}
				<NoColumnsRenderer
					NoColumns={NoColumns}
					hasColumnConfiguration={
						ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
							? canViewColumnConfiguration
							: hasColumnConfiguration
					}
					isLoading={ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? loading : undefined}
				/>
				{!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && loading && <Overlay />}
			</>
		);
	}

	return (
		<Table
			hasFixedTableLayout={hasFixedTableLayout}
			aria-label={formatMessage(messages.issuesTableLabel)}
			ref={tableRef}
		>
			{tableHeader}
			<TableBody
				issueResults={issueResultsData}
				columns={columns}
				tableInjectedProps={tableInjectedProps}
				hasInlineEditing={hasInlineEditing}
				hasFixedTableLayout={hasFixedTableLayout}
				enableRowDragging={enableRowDragging}
			/>
			{loading ? (
				<TableFooter>
					<tr>
						<td>
							<Overlay />
						</td>
					</tr>
				</TableFooter>
			) : null}
		</Table>
	);
};

export default IssueTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Table = styled.table<{ hasFixedTableLayout: boolean }>(
	{
		// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. Please do
		// extensive cross-browser testing before you consider updating this rule.
		height: '1px',
		position: 'relative',
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	() =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && {
			// Using separate borders with no border spacing so that sticky table header can maintain borders on scroll
			// Note: having separate borders should also facilitate adding a border radius to the table if desired
			borderCollapse: 'separate',
			borderSpacing: 0,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasFixedTableLayout }) =>
		// nin-resizable-columns_8z4eo has jsv-one-issue-table-spreadsheet-ui_ygrnf as a prereq, but
		// adding a check here to be cautious. Changing table layout is a dramatic change.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		ff('nin-resizable-columns_8z4eo') &&
		hasFixedTableLayout && {
			tableLayout: 'fixed',
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableFooter = styled.tfoot({
	border: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	td: {
		padding: 0,
	},
});
